import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import DataTable from "react-data-table-component";
import Header from "./Header";
import Footer from "./Footer";
import API_BASE_URL from "../Config";
import "../styles/global.css";
import "./styles/TrackOrder.css";
/* ────────── SVG Icon System ────────── */
const Icon = ({ d, size = 18, stroke = 1.8 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={stroke}
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ display: "block", flexShrink: 0 }}
  >
    {d}
  </svg>
);

const TruckIcon = (p) => (
  <Icon {...p} d={<><rect x="1" y="3" width="15" height="13" rx="1" /><path d="M16 8h4l3 3v5h-7V8z" /><circle cx="5.5" cy="18.5" r="2.5" /><circle cx="18.5" cy="18.5" r="2.5" /></>} />
);
const PlusIcon = (p) => (
  <Icon {...p} d={<><path d="M12 5v14M5 12h14" /></>} />
);
const TrashIcon = (p) => (
  <Icon {...p} d={<><path d="M3 6h18" /><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /><path d="M19 6 18 20a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6M14 11v6" /></>} />
);
const CloseIcon = (p) => (
  <Icon {...p} d={<><path d="M18 6 6 18M6 6l12 12" /></>} />
);
const CheckCircleIcon = (p) => (
  <Icon {...p} d={<><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></>} />
);
const ClockIcon = (p) => (
  <Icon {...p} d={<><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></>} />
);
const PackageIcon = (p) => (
  <Icon {...p} d={<><line x1="16.5" y1="9.4" x2="7.5" y2="4.21" /><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" /><polyline points="3.27 6.96 12 12.01 20.73 6.96" /><line x1="12" y1="22.08" x2="12" y2="12" /></>} />
);
const MapPinIcon = (p) => (
  <Icon {...p} d={<><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" /></>} />
);
const AlertCircleIcon = (p) => (
  <Icon {...p} d={<><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></>} />
);

/* ────────── Custom Confirm Dialog ────────── */
const ConfirmDialog = ({ open, title, message, confirmLabel = "Confirm", confirmVariant = "danger", onConfirm, onCancel }) => {
  if (!open) return null;
  return (
    <div className="cdlg-overlay" role="dialog" aria-modal="true" aria-labelledby="cdlg-title">
      <div className="cdlg-box">
        <div className={`cdlg-icon-wrap cdlg-icon-${confirmVariant}`}>
          {confirmVariant === "danger" ? (
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
              <path d="M10 11v6M14 11v6" />
              <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
            </svg>
          ) : (
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          )}
        </div>
        <h3 id="cdlg-title" className="cdlg-title">{title}</h3>
        <p className="cdlg-message">{message}</p>
        <div className="cdlg-actions">
          <button type="button" className="cdlg-btn cdlg-btn-cancel" onClick={onCancel}>
            Cancel
          </button>
          <button type="button" className={`cdlg-btn cdlg-btn-confirm cdlg-btn-${confirmVariant}`} onClick={onConfirm}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ── Status config ── */
const STATUS_CONFIG = {
  "Confirmed Order":   { color: "#2563eb", bg: "#eff6ff", border: "#bfdbfe", icon: <CheckCircleIcon size={14} /> },
  "Processing Order":  { color: "#d97706", bg: "#fffbeb", border: "#fde68a", icon: <ClockIcon size={14} /> },
  "Quality Check":     { color: "#7c3aed", bg: "#f5f3ff", border: "#ddd6fe", icon: <PackageIcon size={14} /> },
  "Product Dispatched":{ color: "#0891b2", bg: "#ecfeff", border: "#a5f3fc", icon: <TruckIcon size={14} /> },
  "Product Delivered": { color: "#059669", bg: "#ecfdf5", border: "#6ee7b7", icon: <MapPinIcon size={14} /> },
};

const getStatusStyle = (status) =>
  STATUS_CONFIG[status] || { color: "#6b7a74", bg: "#f3faf6", border: "#d1d5db", icon: <ClockIcon size={14} /> };

/* ────────── Main Component ────────── */
const TrackOrder = () => {
  const { orderid } = useParams();
  const [loading, setLoading] = useState(false);
  const [trackOrders, setTrackOrders] = useState([]);
  const [messageAlert, setMessageAlert] = useState("");
  const [messageType, setMessageType] = useState("");
  const [trackStatus, setTrackStatus] = useState("");
  const [trackDescription, setTrackDescription] = useState("");
  const [showModal, setShowModal] = useState(false);

  /* ── Confirm dialog state ── */
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: "",
    message: "",
    confirmLabel: "Confirm",
    confirmVariant: "danger",
    onConfirm: null,
  });

  const showConfirm = useCallback(({ title, message, onConfirm, confirmLabel = "Confirm", confirmVariant = "danger" }) => {
    setConfirmDialog({ open: true, title, message, onConfirm, confirmLabel, confirmVariant });
  }, []);

  const closeConfirm = useCallback(() => {
    setConfirmDialog((prev) => ({ ...prev, open: false, onConfirm: null }));
  }, []);

  const token = localStorage.getItem("authToken");

  /* ── Fetch ── */
  const fetchTrackOrders = useCallback(() => {
    setLoading(true);
    fetch(`${API_BASE_URL}api/trackorder/${orderid}`, {
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${token}`,
      },
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status) {
          setTrackOrders(data.data);
        } else {
          setTrackOrders([]);
        }
      })
      .catch((err) => console.error("Error fetching:", err))
      .finally(() => setLoading(false));
  }, [orderid, token]);

  useEffect(() => {
    fetchTrackOrders();
  }, [orderid]);

  /* ── Flash message ── */
  const flashMessage = (msg, type = "success") => {
    setMessageAlert(msg);
    setMessageType(type);
    setTimeout(() => {
      setMessageAlert("");
      setMessageType("");
    }, 4000);
  };

  /* ── Add Track ── */
  const handleAddTrack = (e) => {
    e.preventDefault();
    setLoading(true);

    const formData = {
      orderid: orderid,
      trackstatus: trackStatus,
      trackdescription: trackDescription,
    };

    fetch(`${API_BASE_URL}api/trackorder`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(formData),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status) {
          flashMessage("Track order added successfully!", "success");
          fetchTrackOrders();
          setTrackStatus("");
          setTrackDescription("");
          setShowModal(false);
        } else {
          flashMessage("Failed to add track order.", "danger");
        }
      })
      .catch(() => flashMessage("Something went wrong.", "danger"))
      .finally(() => setLoading(false));
  };

  /* ── Delete (now using custom confirm dialog) ── */
  const handleDelete = (id) => {
    showConfirm({
      title: "Delete Track Event",
      message: "Are you sure you want to delete this tracking record? This action cannot be undone.",
      confirmLabel: "Yes, Delete",
      confirmVariant: "danger",
      onConfirm: () => {
        closeConfirm();
        setLoading(true);
        fetch(`${API_BASE_URL}api/trackorder/${id}`, {
          method: "GET",
          headers: { Authorization: `Bearer ${token}` },
        })
          .then((res) => res.json())
          .then((data) => {
            if (data.status) {
              flashMessage("Track order deleted successfully!", "success");
              fetchTrackOrders();
            } else {
              flashMessage("Failed to delete record.", "danger");
            }
          })
          .catch(() => flashMessage("Something went wrong.", "danger"))
          .finally(() => setLoading(false));
      },
    });
  };

  /* ── Table Columns ── */
  const columns = [
    {
      name: "Track Status",
      selector: (row) => row.trackstatus,
      sortable: true,
      cell: (row) => {
        const cfg = getStatusStyle(row.trackstatus);
        return (
          <span
            className="to-status-pill"
            style={{ background: cfg.bg, color: cfg.color, borderColor: cfg.border }}
          >
            <span className="to-status-pill__icon" style={{ color: cfg.color }}>{cfg.icon}</span>
            {row.trackstatus}
          </span>
        );
      },
      grow: 1,
    },
    {
      name: "Description",
      selector: (row) => row.trackdescription,
      cell: (row) => <span className="to-cell-desc">{row.trackdescription || "—"}</span>,
      grow: 2,
    },
    {
      name: "Created At",
      selector: (row) => row.created_at,
      cell: (row) => (
        <span className="to-cell-muted">
          {new Date(row.created_at).toLocaleString("en-IN", {
            day: "2-digit", month: "short", year: "numeric",
            hour: "2-digit", minute: "2-digit",
          })}
        </span>
      ),
      sortable: true,
      width: "200px",
    },
    {
      name: "Action",
      cell: (row) => (
        <button
          className="to-ico-btn del"
          onClick={() => handleDelete(row.id)}
          title="Delete"
        >
          <TrashIcon size={15} />
        </button>
      ),
      width: "80px",
      right: true,
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
  ];

  /* ── DataTable Styles ── */
  const tableStyles = {
    table: { style: { background: "transparent" } },
    headRow: {
      style: {
        background: "#f0fbf6",
        borderTopLeftRadius: "12px",
        borderTopRightRadius: "12px",
        borderBottom: "1px solid #d6efe2",
        minHeight: "52px",
      },
    },
    headCells: {
      style: {
        fontSize: "11.5px",
        fontWeight: 700,
        letterSpacing: "0.07em",
        textTransform: "uppercase",
        color: "#5b6b66",
        paddingLeft: "18px",
        paddingRight: "18px",
      },
    },
    rows: {
      style: {
        minHeight: "68px",
        fontSize: "14px",
        color: "#1f2a37",
        borderBottom: "1px solid #eef4f1 !important",
        transition: "background 0.18s ease",
      },
      highlightOnHoverStyle: {
        background: "#f6fcf9",
        borderBottomColor: "#e3f0e9",
        outline: "none",
      },
      stripedStyle: { background: "#fafefb" },
    },
    cells: { style: { paddingLeft: "18px", paddingRight: "18px" } },
    pagination: {
      style: {
        borderTop: "1px solid #eef4f1",
        color: "#5b6b66",
        fontSize: "13px",
        minHeight: "56px",
        paddingLeft: "18px",
        paddingRight: "18px",
      },
      pageButtonsStyle: {
        borderRadius: "8px",
        height: "34px",
        minWidth: "34px",
        cursor: "pointer",
        transition: "all .15s ease",
        color: "#1f2a37",
        fill: "#1f2a37",
        backgroundColor: "transparent",
        border: "1px solid #e3f0e9",
        marginLeft: "4px",
      },
    },
  };

  /* ── Timeline sorted (newest last for visual flow) ── */
  const sortedForTimeline = [...trackOrders].sort(
    (a, b) => new Date(a.created_at) - new Date(b.created_at)
  );
  const latestStatus = sortedForTimeline[sortedForTimeline.length - 1]?.trackstatus || "";

  return (
    <>
      <div id="layout-wrapper">
        <Header />

        {loading && (
          <div className="to-overlay">
            <div className="to-spinner" />
          </div>
        )}

        <div className="main-content">
          <div className="page-content">
            <div className="to-shell">

              {/* ── Page Header ── */}
              <div className="to-head">
                <div className="to-head-left">
                  <div className="to-head-icon">
                    <TruckIcon size={24} />
                  </div>
                  <div>
                    <h2>
                      Track Order&nbsp;
                      <span className="to-orderid-badge">#{orderid}</span>
                    </h2>
                    <p>View and manage all tracking events for this order.</p>
                  </div>
                </div>
                <button
                  type="button"
                  className="to-add-btn"
                  onClick={() => setShowModal(true)}
                >
                  <PlusIcon size={18} />
                  Add Track
                </button>
              </div>

              {/* ── Alert ── */}
              {messageAlert && (
                <div className={`to-alert ${messageType}`} role="alert">
                  <span>{messageAlert}</span>
                  <button type="button" onClick={() => setMessageAlert("")} aria-label="Close">
                    <CloseIcon size={16} />
                  </button>
                </div>
              )}

              {/* ── Two-column layout ── */}
              <div className="to-layout">

                {/* ── Timeline Panel ── */}
                <div className="to-card">
                  <div className="to-card-header">
                    <MapPinIcon size={16} />
                    <h3>Order Timeline</h3>
                  </div>
                  <div className="to-timeline">
                    {sortedForTimeline.length === 0 ? (
                      <div className="to-timeline-empty">
                        <TruckIcon size={36} />
                        <p style={{ margin: 0, fontSize: 14 }}>No events yet</p>
                        <span style={{ fontSize: 12 }}>Add a track to get started</span>
                      </div>
                    ) : (
                      <ul className="to-timeline-list">
                        {sortedForTimeline.map((item, idx) => {
                          const cfg = getStatusStyle(item.trackstatus);
                          const isLatest = idx === sortedForTimeline.length - 1;
                          return (
                            <li
                              key={item.id}
                              className={`to-tl-item ${isLatest ? "is-latest" : ""}`}
                              style={{ animationDelay: `${idx * 0.06}s` }}
                            >
                              <div
                                className="to-tl-dot"
                                style={{ background: cfg.color }}
                              >
                                <span style={{ display: "flex" }}>{cfg.icon}</span>
                              </div>
                              <div className="to-tl-content">
                                <div
                                  className="to-tl-badge"
                                  style={{
                                    background: cfg.bg,
                                    color: cfg.color,
                                    borderColor: cfg.border,
                                  }}
                                >
                                  <span style={{ display: "flex", color: cfg.color }}>{cfg.icon}</span>
                                  {item.trackstatus}
                                </div>
                                {item.trackdescription && (
                                  <div className="to-tl-desc">{item.trackdescription}</div>
                                )}
                                <div className="to-tl-time">
                                  {new Date(item.created_at).toLocaleString("en-IN", {
                                    day: "2-digit", month: "short", year: "numeric",
                                    hour: "2-digit", minute: "2-digit",
                                  })}
                                </div>
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                </div>

                {/* ── Table Panel ── */}
                <div className="to-card to-table-card">
                  <div className="to-card-header">
                    <PackageIcon size={16} />
                    <h3>Track Records</h3>
                    <div style={{ marginLeft: "auto" }}>
                      <span className="to-count-badge">
                        Total:&nbsp;<b>{trackOrders.length}</b>
                      </span>
                    </div>
                  </div>

                  <DataTable
                    columns={columns}
                    data={trackOrders}
                    progressPending={loading}
                    customStyles={tableStyles}
                    pagination
                    highlightOnHover
                    striped
                    responsive
                    noDataComponent={
                      <div className="to-empty">
                        <TruckIcon size={40} />
                        <p>No track orders found</p>
                        <span>Click "Add Track" to add the first event</span>
                      </div>
                    }
                  />
                </div>

              </div>{/* end .to-layout */}
            </div>
          </div>

          {/* ═══════════ ADD TRACK MODAL ═══════════ */}
          {showModal && (
            <div
              className="to-modal-overlay"
              onClick={(e) => { if (e.target === e.currentTarget) setShowModal(false); }}
              aria-modal="true"
              role="dialog"
              aria-labelledby="toModalTitle"
            >
              <div className="to-modal">
                {/* Header */}
                <div className="to-modal__header">
                  <div className="to-modal__header-left">
                    <div className="to-modal__icon">
                      <TruckIcon size={20} />
                    </div>
                    <div>
                      <p className="to-modal__title" id="toModalTitle">Add Track Event</p>
                      <p className="to-modal__subtitle">Order #{orderid}</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="to-modal__close"
                    onClick={() => setShowModal(false)}
                    aria-label="Close modal"
                  >
                    <CloseIcon size={16} />
                  </button>
                </div>

                {/* Body */}
                <form onSubmit={handleAddTrack}>
                  <div className="to-modal__body">

                    {/* Track Status */}
                    <div className="to-form-group">
                      <label className="to-form-label" htmlFor="toTrackStatus">
                        Track Status <span>*</span>
                      </label>
                      <select
                        id="toTrackStatus"
                        className="to-form-select"
                        value={trackStatus}
                        onChange={(e) => setTrackStatus(e.target.value)}
                        required
                      >
                        <option value="">-- Select Status --</option>
                        <option value="Confirmed Order">Confirmed Order</option>
                        <option value="Processing Order">Processing Order</option>
                        <option value="Quality Check">Quality Check</option>
                        <option value="Product Dispatched">Product Dispatched</option>
                        <option value="Product Delivered">Product Delivered</option>
                      </select>

                      {/* Live preview of selected status */}
                      {trackStatus && (() => {
                        const cfg = getStatusStyle(trackStatus);
                        return (
                          <div
                            className="to-select-preview"
                            style={{ background: cfg.bg, borderColor: cfg.border, color: cfg.color }}
                          >
                            <span style={{ display: "flex", color: cfg.color }}>{cfg.icon}</span>
                            <span style={{ fontWeight: 600, fontSize: 13 }}>{trackStatus}</span>
                          </div>
                        );
                      })()}
                    </div>

                    {/* Description */}
                    <div className="to-form-group">
                      <label className="to-form-label" htmlFor="toTrackDesc">
                        Description
                      </label>
                      <textarea
                        id="toTrackDesc"
                        className="to-form-control"
                        rows="3"
                        placeholder="Add notes about this tracking event…"
                        value={trackDescription}
                        onChange={(e) => setTrackDescription(e.target.value)}
                      />
                    </div>

                  </div>

                  {/* Footer */}
                  <div className="to-modal__footer">
                    <button
                      type="button"
                      className="to-btn-cancel"
                      onClick={() => setShowModal(false)}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="to-btn-submit"
                      disabled={loading}
                    >
                      <PlusIcon size={15} />
                      {loading ? "Saving…" : "Save Track"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* ────────── Custom Confirm Dialog (replaces window.confirm) ────────── */}
          <ConfirmDialog
            open={confirmDialog.open}
            title={confirmDialog.title}
            message={confirmDialog.message}
            confirmLabel={confirmDialog.confirmLabel}
            confirmVariant={confirmDialog.confirmVariant}
            onConfirm={confirmDialog.onConfirm}
            onCancel={closeConfirm}
          />

          <Footer />
        </div>
      </div>
    </>
  );
};

export default TrackOrder;